import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SectionStamp } from '../components/Atoms';
import Icon from '../components/Icon';
import TopBar from '../components/TopBar';
import TabBar from '../components/TabBar';
import WhatsAppFab from '../components/WhatsAppFab';
import { fonts } from '../theme';

const SERVICES = [
  {
    ic: 'spine', n: '01', title: 'Orthopaedic',
    lead: 'Musculoskeletal injuries, joint pain, post-surgical recovery.',
    details: 'Restoring joint health, reducing pain, and enhancing mobility through personalised exercise programmes, manual therapy and modern equipment.',
    tags: ['Manual therapy', 'Strengthening', 'Sports'],
  },
  {
    ic: 'brain', n: '02', title: 'Neurological',
    lead: 'Helping patients regain strength and coordination.',
    details: 'Evidence-based therapies for stroke, multiple sclerosis and Parkinson’s. Plans focus on motor function, balance and quality of life.',
    tags: ['Stroke', 'MS', 'Parkinson’s'],
  },
  {
    ic: 'baby', n: '03', title: 'Paediatric',
    lead: 'Specialised care for young patients with unique needs.',
    details: 'Therapy for developmental delays, congenital disorders and injuries — engaging programmes tailored to each child’s capacity.',
    tags: ['Developmental', 'Congenital', 'Mobility'],
  },
  {
    ic: 'cane', n: '04', title: 'Geriatric',
    lead: 'Mobility, balance, and overall health for seniors.',
    details: 'Helping older adults stay active, reduce fall risk and improve quality of life through balance and strength training.',
    tags: ['Balance', 'Falls', 'Strength'],
  },
  {
    ic: 'bandage', n: '05', title: 'Post-op rehab',
    lead: 'Joint replacements and other post-surgical recovery.',
    details: 'Accelerate recovery, reduce pain and restore function after surgery — with plans timed to your healing protocol.',
    tags: ['Knee', 'Hip', 'Shoulder'],
  },
];

export default function Services({ t }) {
  const [open, setOpen] = useState(0);
  const navigate = useNavigate();

  return (
    <div style={{
      width: '100%', height: '100%', position: 'relative',
      overflow: 'hidden', background: t.paper, color: t.ink,
    }}>
      <TopBar t={t} title="Care" />

      <div style={{
        position: 'absolute', top: 50, left: 0, right: 0, bottom: 0,
        overflowY: 'auto', padding: '8px 22px 100px',
      }}>
        <SectionStamp n="—" label="Specialties" t={t} />
        <h1 style={{
          fontFamily: fonts.serif, fontWeight: 300,
          fontSize: 40, lineHeight: 0.95, letterSpacing: '-0.035em',
          margin: '14px 0 8px',
        }}>
          What I <span style={{ fontStyle: 'italic', color: t.mintDeep }}>treat.</span>
        </h1>
        <p style={{
          fontFamily: fonts.sans, fontSize: 13, color: t.muted, lineHeight: 1.5,
          margin: '0 0 22px', maxWidth: 290,
        }}>
          Five specialties, a single method: assess hands-on, plan around you, revise every visit.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {SERVICES.map((s, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                role="button"
                tabIndex={0}
                onClick={() => setOpen(isOpen ? -1 : i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOpen(isOpen ? -1 : i);
                  }
                }}
                style={{
                  textAlign: 'left', cursor: 'pointer', outline: 'none',
                  background: isOpen ? t.ink : 'transparent',
                  color: isOpen ? t.paper : t.ink,
                  border: `0.5px solid ${isOpen ? t.ink : t.hair}`,
                  borderRadius: 14, padding: 16, transition: 'background .2s, color .2s',
                  display: 'flex', flexDirection: 'column', gap: 6,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{
                    fontFamily: fonts.mono, fontSize: 10, letterSpacing: '0.16em',
                    color: isOpen ? t.mint : t.muted,
                  }}>{s.n}</span>
                  <Icon name={s.ic} size={18} stroke={isOpen ? t.mint : t.ink} sw={1.6}/>
                  <span style={{
                    fontFamily: fonts.serif, fontSize: 22, fontWeight: 400,
                    letterSpacing: '-0.02em', flex: 1,
                  }}>{s.title}</span>
                  <Icon name={isOpen ? 'minus' : 'plus'} size={16} stroke={isOpen ? t.paper : t.ink}/>
                </div>
                <div style={{
                  fontFamily: fonts.sans, fontSize: 12.5,
                  color: isOpen ? 'rgba(246,243,236,0.75)' : t.muted, lineHeight: 1.45,
                }}>{s.lead}</div>

                {isOpen && (
                  <div style={{
                    marginTop: 8, borderTop: `0.5px solid rgba(168,230,200,0.3)`, paddingTop: 12,
                  }}>
                    <div style={{
                      fontFamily: fonts.sans, fontSize: 12.5, lineHeight: 1.55,
                      color: 'rgba(246,243,236,0.85)',
                    }}>{s.details}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
                      {s.tags.map(tag => (
                        <span key={tag} style={{
                          fontFamily: fonts.mono, fontSize: 9.5, letterSpacing: '0.12em',
                          textTransform: 'uppercase', color: t.mint,
                          border: `0.5px solid ${t.mint}66`, borderRadius: 999,
                          padding: '4px 9px',
                        }}>{tag}</span>
                      ))}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate('/book'); }}
                      style={{
                        appearance: 'none', border: 'none', cursor: 'pointer',
                        background: t.mint, color: t.ink,
                        padding: '10px 14px', borderRadius: 999, marginTop: 14,
                        fontFamily: fonts.sans, fontSize: 12, fontWeight: 500,
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      Book this care <Icon name="arrow-up-right" size={14}/>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <WhatsAppFab bottom={100}/>
      <TabBar t={t}/>
    </div>
  );
}
